import type { Witness } from '../store/booksStore'
import type { LedgerSafeEditorialEvent } from './ledgerSafeEditorialEvents'
import type {
  LedgerSafeLedgerRecord,
  LedgerSafeLedgerStateRecord,
} from './ledgerSafeLedgers'
import type {
  LedgerSafeContributor,
  LedgerSafeForkHistoryEntry,
  LedgerSafeHumanContributor,
  LedgerSafeSharedPermissions,
} from './ledgerSafeProvenance'
import type { ChapterStructuralRangeLayer } from './structuralRangeTypes'

export const LEDGER_SAFE_SHARED_SNAPSHOT_FORMAT_VERSION = 'ledger-safe-shared-snapshot.v2' as const
export const LEDGER_SAFE_SHARED_SNAPSHOT_LEGACY_FORMAT_VERSION = 'ledger-safe-shared-snapshot.v1' as const

export interface LedgerSafeSharedSnapshotEntity {
  id: string
  name: string
  description?: string
}

export interface LedgerSafeSharedSnapshotTransportManifest {
  profile: 'compact-web-share.v1'
  stateWitnessSegmentation: {
    policy: 'rehydrate-from-witnesses'
    strippedSnapshotCount: number
  }
  editorialEvents: {
    policy: 'manifest-only'
    originalCount: number
    exportedCount: number
    omittedFields: Array<'beforeRows' | 'afterRows' | 'operation' | 'toolCallProjection' | 'uiContext'>
    operationCounts: Record<string, number>
    contributorsPreservedInProvenance: boolean
  }
}

export interface LedgerSafeSharedSnapshotV1 {
  format_version: typeof LEDGER_SAFE_SHARED_SNAPSHOT_LEGACY_FORMAT_VERSION
  snapshot_schema_version: 1
  created_at: string
  source_app: {
    name: 'segmentation-agent'
    version?: string
    release_name?: string
  }
  project: LedgerSafeSharedSnapshotEntity
  work: LedgerSafeSharedSnapshotEntity
  context: LedgerSafeSharedSnapshotEntity & {
    label_prefix?: string
  }
  ledger: LedgerSafeLedgerRecord
  states: {
    active_kind: 'baseline' | 'working'
    active_state_id: string
    baseline: LedgerSafeLedgerStateRecord
    working?: LedgerSafeLedgerStateRecord | null
  }
  witnesses: Witness[]
  editorial_events: LedgerSafeEditorialEvent[]
  transport?: LedgerSafeSharedSnapshotTransportManifest
  permissions?: LedgerSafeSharedPermissions
  provenance: {
    origin: 'local-ledger-safe'
    source_project_id: string
    source_work_id: string
    source_context_id: string
    source_ledger_id: string
    source_active_state_id: string
    created_by?: LedgerSafeHumanContributor
    shared_by?: LedgerSafeHumanContributor
    contributors?: LedgerSafeContributor[]
    fork_history?: LedgerSafeForkHistoryEntry[]
    forked_from?: {
      share_id?: string
      snapshot_hash?: string
      source_ledger_id?: string
    }
  }
  summary: {
    title: string
    project_name: string
    work_name: string
    context_name: string
    ledger_name: string
    witness_count: number
    row_count: number
    editorial_event_count: number
    active_state_kind: 'baseline' | 'working'
    shared_at: string
  }
}

export interface LedgerSafeSharedSnapshotV2 extends Omit<
  LedgerSafeSharedSnapshotV1,
  'format_version' | 'snapshot_schema_version'
> {
  format_version: typeof LEDGER_SAFE_SHARED_SNAPSHOT_FORMAT_VERSION
  snapshot_schema_version: 2
  structural_layer: ChapterStructuralRangeLayer | null
}

export type LedgerSafeSharedSnapshot = LedgerSafeSharedSnapshotV1 | LedgerSafeSharedSnapshotV2

export interface SharedLedgerRecord {
  record_version: string
  share_id: string
  snapshot_hash: string
  created_at: string
  snapshot_format_version: string
  summary?: LedgerSafeSharedSnapshot['summary']
}

export interface PublishSharedLedgerResponse {
  record: SharedLedgerRecord
  share_id: string
  snapshot_hash: string
  share_url: string
}

export interface FetchSharedLedgerResponse {
  record: SharedLedgerRecord
  snapshot: LedgerSafeSharedSnapshot
  share_id: string
  snapshot_hash: string
  capabilities?: {
    canRecoverEditableState?: boolean
    canImportBaseline?: boolean
    requiresAuthenticationForRecovery?: boolean
  }
}

export interface FetchSharedLedgerRecoveryResponse extends FetchSharedLedgerResponse {
  authenticated_user?: {
    user_id: string
    email: string
    display_name: string
    domain: string
    role: string
    workspace_id: string
    provider: string
  }
}
